import { DbTransaction, SQLite, SQLiteObject, SQLiteTransaction } from '@ionic-enterprise/secure-storage'
import { IOrderBy } from './indexDb.service'
import keyVault from './keyVault'
import Logger from './logger.service'
import { IndexableTypeArray, IndexableTypePart } from './databaseUtility.service'
import { getSha256 } from '../utilities'

interface Column {
  name: string
  type: string
}

export class SecureStorageService {
  private dbHandle: SQLiteObject | undefined
  private readyPromise: Promise<void>

  protected constructor(database: string) {
    this.readyPromise = this.initializeSchema(database)
  }

  get handle(): SQLiteObject | undefined {
    return this.dbHandle
  }

  ready(): Promise<void> {
    return this.readyPromise
  }

  private async initializeSchema(database: string): Promise<void> {
    await this.open(database)
  }

  private async open(database: string): Promise<void> {
    try {
      Logger.info(`SecureStorageService: Attempting to open database: ${database}`)

      const key = await keyVault.get(database)
      const hashedKey = await getSha256(key)

      Logger.info(
        `SecureStorageService: Successfully got the value from Identity Vault for database: ${database} - [${hashedKey}]`
      )

      this.dbHandle = await SQLite.create({
        name: database,
        location: 'default',
        key,
      })

      Logger.info(`SecureStorageService: Successfully created a DB Handle to database: ${database}`)
    } catch (error) {
      Logger.errorObj(`SecureStorageService: Error creating a DB Handle to database: ${database}`, error)

      throw error
    }
  }

  protected createTableSQL(name: string, columns: Array<Column>): string {
    let cols = ''
    columns.forEach((c, i) => {
      cols += `${i ? ', ' : ''}${c.name} ${c.type}`
    })
    return `CREATE TABLE IF NOT EXISTS ${name} (${cols})`
  }

  protected createTableIndexSQL(tableName: string): string {
    return `CREATE INDEX IF NOT EXISTS PCRDocs_idx ON ${tableName} (createdBy, createDateTime)`
  }

  protected runSQL(tx: DbTransaction, sql: any, values?: any[], logSuccess = false) {
    try {
      if (!tx) {
        throw new Error(`DEV Error: DbTransaction is not defined - will not run SQL`)
      }

      tx.executeSql(sql, values)

      if (logSuccess) {
        Logger.info(`Successfully ran SQL: [${sql}]`)
      }
    } catch (error) {
      Logger.errorObj(`Error running SQL: [${sql}]`, error)

      throw error
    }
  }

  async getAll<T>(table: string): Promise<Array<T>> {
    const documents: Array<T> = []

    await this.ready()

    await this.handle?.transaction((tx) =>
      tx.executeSql(
        `SELECT * FROM ${table}`,
        [],
        // tslint:disable-next-line:variable-name
        (_t: SQLiteTransaction, r: any) => {
          for (let i = 0; i < r.rows.length; i++) {
            documents.push(r.rows.item(i))
          }
        }
      )
    )
    return documents
  }

  async getWhereAttributeEquals<T>(
    attribute: string,
    value: IndexableTypePart | IndexableTypeArray,
    table: string,
    orderBy?: IOrderBy
  ): Promise<T[]> {
    const documents: Array<T> = []

    await this.ready()

    const id = { name: 'id', type: 'INTEGER PRIMARY KEY' }
    const createdBy = { name: 'createdBy', type: 'TEXT' }
    const createDateTime = { name: 'createDateTime', type: 'INTEGER' }
    const data = { name: 'data', type: 'TEXT' }

    await this.handle?.transaction((tx) => {
      this.runSQL(tx, this.createTableSQL(`${table}`, [id, createdBy, createDateTime, data]), undefined, true)
      this.runSQL(tx, this.createTableIndexSQL(`${table}`), undefined, true)
    })

    const countCheck = await this.getPcrCountByCreatedBy(table, value)
    Logger.info(`Number of PCRs createdBy [${value.toString()}] before getting all records = ${countCheck}`)

    await this.handle?.transaction((tx) =>
      tx.executeSql(
        `SELECT * FROM ${table} where ${attribute} = ${value} ${
          orderBy !== undefined ? ` ORDER BY ${orderBy.orderBy} ${orderBy.asc ? 'ASC' : 'DESC'}` : ''
        }`,
        [],
        // tslint:disable-next-line:variable-name
        (_t: SQLiteTransaction, r: any) => {
          for (let i = 0; i < r.rows.length; i++) {
            documents.push(r.rows.item(i))
            Logger.info(`Added record ${i} to document list`)
          }
        }
      )
    )

    Logger.info(`Number of actual documents added to the list, createdBy [${value.toString()}] = ${documents.length}`)

    return documents
  }

  async get(id: number | string, table: string): Promise<any | null> {
    let document: any | null = null

    await this.ready()

    await this.handle?.transaction((tx) => {
      tx.executeSql(
        `SELECT * FROM ${table} WHERE id = ?`,
        [+id],
        // tslint:disable-next-line:variable-name
        (_t: SQLiteTransaction, r: any) => {
          if (r.rows.length) {
            document = { ...r.rows.item(0) }
          }
        }
      )
    })
    return document
  }

  async remove(id: number | string, table: string): Promise<void> {
    await this.ready()

    await this.handle?.transaction((tx) => this.runSQL(tx, `DELETE FROM ${table} WHERE id = ?`, [+id], true))
  }

  async getPcrCountByCreatedBy(tableName: string, createdBy: IndexableTypePart | IndexableTypeArray): Promise<number> {
    let count = 0

    try {
      const countSql = `SELECT COUNT(1) AS recordCount FROM ${tableName} WHERE createdBy = ?`

      await this.ready()

      await this.handle?.transaction((tx) => {
        tx.executeSql(
          countSql,
          [createdBy],
          // tslint:disable-next-line:variable-name
          (_t: SQLiteTransaction, result: any) => {
            if (result.rows.length) {
              count = result.rows.item(0).recordCount
            }
          }
        )
      })
    } catch (error) {
      Logger.errorObj(`Error running getPcrCountByCreatedBy for ${createdBy.toString()} `, error)
    }

    return count
  }

  async getCountsGroupedByCreatedBy<T>(tableName: string): Promise<T[]> {
    const records: Array<T> = []

    try {
      const countSql = `SELECT createdBy, COUNT(1) AS recordCount FROM ${tableName} GROUP BY createdBy`

      await this.ready()

      await this.handle?.transaction((tx) => {
        tx.executeSql(
          countSql,
          [],
          // tslint:disable-next-line:variable-name
          (_t: SQLiteTransaction, result: any) => {
            for (let i = 0; i < result.rows.length; i++) {
              records.push(result.rows.item(i))
            }
          }
        )
      })
    } catch (error) {
      Logger.errorObj(`Error running getCountsGroupedByCreatedBy for ${tableName} `, error)
    }

    return records
  }
}
